import React, { useEffect, useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, ShieldCheck } from '@phosphor-icons/react';
import { create3plMerchant, update3plMerchant, getMerchantTrustScores } from '../../lib/api';

const BAND_STYLES = {
  excellent: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  good:      'bg-sky-100 text-sky-800 border-sky-200',
  watch:     'bg-amber-100 text-amber-800 border-amber-200',
  risk:      'bg-rose-100 text-rose-800 border-rose-200',
  no_data:   'bg-slate-100 text-slate-500 border-slate-200',
};

export default function MerchantsTab({ merchants, onRefresh, selected, setSelected }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', contact_email: '', billing_email: '', address: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [trust, setTrust] = useState({}); // merchant_id → {score, band, ...}

  useEffect(() => {
    getMerchantTrustScores({ days: 180 })
      .then((r) => {
        const map = {};
        (r.data?.merchants || []).forEach((m) => { map[m.merchant_id] = m; });
        setTrust(map);
      })
      .catch(() => {});
  }, []);

  const beginCreate = () => {
    setEditing(null);
    setForm({ name: '', contact_email: '', billing_email: '', address: '', notes: '' });
    setOpen(true);
  };
  const beginEdit = (m) => {
    setEditing(m);
    setForm({
      name: m.name || '', contact_email: m.contact_email || '',
      billing_email: m.billing_email || '', address: m.address || '', notes: m.notes || '',
    });
    setOpen(true);
  };
  const submit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      // Build a payload that omits empty strings — backend rejects "" for
      // EmailStr fields (strict pydantic) so we must drop them entirely.
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (typeof v === 'string') {
          const trimmed = v.trim();
          if (trimmed) payload[k] = trimmed;
        }
      });
      if (!payload.name) payload.name = form.name.trim();
      if (editing) await update3plMerchant(editing.id, payload);
      else await create3plMerchant(payload);
      toast.success(editing ? 'Merchant updated' : 'Merchant created');
      setOpen(false);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };
  const close = async (m) => {
    if (!window.confirm(`Close merchant "${m.name}"? They'll stop accruing charges.`)) return;
    try {
      await update3plMerchant(m.id, { status: 'closed' });
      toast.success('Merchant closed');
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Close failed');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold">Merchants</h2>
        <Button onClick={beginCreate} data-testid="merchant-create-btn" size="sm">
          <Plus size={14} className="mr-1" /> New merchant
        </Button>
      </div>
      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 border-b border-border">
            <tr className="text-left">
              <th className="p-2">Name</th>
              <th className="p-2">Contact</th>
              <th className="p-2">Status</th>
              <th className="p-2">Pallet trust</th>
              <th className="p-2 w-32 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {merchants.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">
                No merchants yet — click &quot;New merchant&quot; or use the auto-created Default Merchant.
              </td></tr>
            )}
            {merchants.map((m) => {
              const t = trust[m.id];
              return (
              <tr key={m.id}
                data-testid={`merchant-row-${m.id}`}
                className={`border-b border-border last:border-b-0 cursor-pointer hover:bg-muted/30 ${selected?.id === m.id ? 'bg-sky-50' : ''}`}
                onClick={() => setSelected(m)}>
                <td className="p-2">
                  <div className="font-semibold flex items-center gap-1.5">
                    {m.name}
                    {m.is_default && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-slate-200">Default</span>}
                  </div>
                  {m.address && <div className="text-[11px] text-muted-foreground">{m.address}</div>}
                </td>
                <td className="p-2 text-xs">
                  {m.contact_email && <div>{m.contact_email}</div>}
                  {m.billing_email && <div className="text-muted-foreground">Bill: {m.billing_email}</div>}
                </td>
                <td className="p-2">
                  <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 ${m.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'}`}>
                    {m.status}
                  </span>
                </td>
                <td className="p-2" data-testid={`merchant-trust-${m.id}`}>
                  {t ? (
                    <div className="flex items-center gap-1.5" title={`${t.movements} pallet movements over 180 days · ${t.damaged} damaged · ${t.missing} missing · ${t.still_out} still out`}>
                      <ShieldCheck size={14} weight="fill" className={`shrink-0 ${t.band === 'excellent' ? 'text-emerald-600' : t.band === 'good' ? 'text-sky-600' : t.band === 'watch' ? 'text-amber-600' : 'text-rose-600'}`} />
                      <span className="font-mono font-bold text-sm">{t.score}</span>
                      <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 border ${BAND_STYLES[t.band]}`}>{t.band === 'no_data' ? 'no data' : t.band}</span>
                    </div>
                  ) : (
                    <span className="text-[10px] text-muted-foreground font-mono">—</span>
                  )}
                </td>
                <td className="p-2 text-right">
                  <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); beginEdit(m); }} data-testid={`merchant-edit-${m.id}`}>
                    Edit
                  </Button>
                  {m.status === 'active' && !m.is_default && (
                    <Button size="sm" variant="outline" className="ml-1 text-rose-700" onClick={(e) => { e.stopPropagation(); close(m); }} data-testid={`merchant-close-${m.id}`}>
                      Close
                    </Button>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="merchant-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.name}` : 'New merchant'}</DialogTitle>
            <DialogDescription>
              Sub-clients of your 3PL. Each merchant gets their own rate card and invoice run.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="merchant-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Contact email</Label>
                <Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="merchant-form-contact-email" />
              </div>
              <div>
                <Label>Billing email</Label>
                <Input value={form.billing_email} onChange={(e) => setForm({ ...form, billing_email: e.target.value })} data-testid="merchant-form-billing-email" />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="merchant-form-address" />
            </div>
            <div>
              <Label>Notes</Label>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="merchant-form-notes"
                className="w-full border border-border bg-background p-2 text-sm" rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="merchant-form-submit">
              {saving ? 'Saving…' : (editing ? 'Save changes' : 'Create merchant')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
